import { Injectable } from "@angular/core";
import * as qz from "qz-tray";
import { ImpressaoEventService } from "../events/impressao-event.service";
import { DriverEscPos } from "../escpos/driverEscPos";
import { ApiService, EventoConfirmacaoPedido } from "./api.service";
import { AppService } from "./app.service";
import { ConfiguracaoMonitor } from "../models/configuracaoMonitor";
import { Subject } from "rxjs";

export type ModoRepeticaoSomPedidoPendente = "LIGADO" | "DESLIGADO";
export type SomNotificao =
  | "default"
  | "nenhum"
  | "sound_1"
  | "sound_2"
  | "sound_3"
  | "sound_4"
  | "sound_5"
  | "sound_6"
  | "sound_7"
  | "sound_8";

export interface ConfiguracaoNotificacao {
  repetirSomPedidoPendente: ModoRepeticaoSomPedidoPendente;
  repetirSomPedidoPendentePeriodo: number | string;
  tempoPermanenciaNoMonitor: number | string;
  notificacao: string;
  numeroRepeticoes: number | string;
  volume: number;
}

@Injectable({
  providedIn: "root",
})
export class NotificacaoService {
  private readonly CHAVE_LOCAL_STORAGE = "configuracao-notificacao";

  configuracao: ConfiguracaoNotificacao;

  eventoNoticacaoPedidoPedente$ = new Subject<boolean>();

  private _repetidorSom: any;

  private _estaTocandoSom: boolean = false;

  private _temporizadorNotificacaoPedidoPendente: any;

  private readonly configuracaoPadrao: ConfiguracaoNotificacao = {
    repetirSomPedidoPendente: "LIGADO",
    repetirSomPedidoPendentePeriodo: "300",
    tempoPermanenciaNoMonitor: "24",
    notificacao: "default",
    numeroRepeticoes: "1",
    volume: 100,
  };

  constructor(private appService: AppService) {
    this.restaurarConfiguracao();

    setTimeout(() => {
      this.resetarTemporizadorNotificacaoPedidoPendente();
    }, 500);
  }

  async restaurarConfiguracao() {
    // Tenta pegar a configuração salva no localStorage
    let configuracao = await this.restaurarConfiguracaoSalvaNoLocalStorage();
    if (!!configuracao) {
      return this.salvarConfiguracao(configuracao);
    }

    // Se não encontrar no localStorage tenta pegar da configuração no portal
    configuracao = await this.tentarRestaurarConfiguracaoDosDadosDaLoja();
    if (!!configuracao) {
      return this.salvarConfiguracao(configuracao);
    }

    // Por último seta a configuração padrão
    this.salvarConfiguracao(this.configuracaoPadrao);
  }

  private resetarTemporizadorNotificacaoPedidoPendente() {
    console.log(
      "tempo de notificação: ",
      this.periodoRepeticaoNotificacaoPendente
    );

    if (!!this._temporizadorNotificacaoPedidoPendente) {
      clearInterval(this._temporizadorNotificacaoPedidoPendente);
    }

    if (!this.notificacaoPedidoPendenteEstaHabilitada) {
      return;
    }

    this._temporizadorNotificacaoPedidoPendente = setInterval(() => {
      this.eventoNoticacaoPedidoPedente$.next(true);
    }, this.periodoRepeticaoNotificacaoPendente);
  }

  private restaurarConfiguracaoSalvaNoLocalStorage(): Promise<ConfiguracaoNotificacao> {
    const configuracao = localStorage.getItem(this.CHAVE_LOCAL_STORAGE);
    if (!configuracao) {
      return Promise.resolve(null);
    }
    try {
      const configuracaoJSON = JSON.parse(
        configuracao
      ) as ConfiguracaoNotificacao;
      return Promise.resolve(configuracaoJSON);
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  private tentarRestaurarConfiguracaoDosDadosDaLoja(): Promise<ConfiguracaoNotificacao> {
    const configuracao = this.appService
      .configuracaoMonitor as ConfiguracaoMonitor;
    if (!configuracao) {
      return Promise.resolve(null);
    }
    try {
      const configuracaoNotificacao: ConfiguracaoNotificacao = {
        notificacao: configuracao.NotificacaoSound,
        repetirSomPedidoPendentePeriodo: 300,
        numeroRepeticoes: Number(configuracao.RepetirSound),
        volume: Number(configuracao.VolumeSound) * 100,
        repetirSomPedidoPendente: "LIGADO",
        tempoPermanenciaNoMonitor: "24",
      };
      return Promise.resolve(configuracaoNotificacao);
    } catch (e) {
      return Promise.resolve(null);
    }
  }

  salvarConfiguracao(configuracao: ConfiguracaoNotificacao) {
    if (!configuracao.tempoPermanenciaNoMonitor) {
      configuracao.tempoPermanenciaNoMonitor = "24";
    }
    this.configuracao = configuracao;
    localStorage.setItem(
      this.CHAVE_LOCAL_STORAGE,
      JSON.stringify(configuracao)
    );
    this.resetarTemporizadorNotificacaoPedidoPendente();
  }

  notificar() {
    if (!this.appService.somMutado) {
      this.tocarSom();
    }
  }

  get tempoPermanenciaNoMonitorEmMilisegundos(): number {
    return this.tempoPermanenciaNoMonitorEmHoras * 60 * 60 * 1000;
  }

  get tempoPermanenciaNoMonitorEmHoras(): number {
    let horas = Number(this?.configuracao?.tempoPermanenciaNoMonitor) ?? null;
    return isNaN(horas) ? 24 : horas;
  }

  get volume(): number {
    let volume = Number(this.configuracao?.volume) ?? 100;
    if (isNaN(volume) || volume < 0 || volume > 100) {
      volume = 100;
    }
    return volume / 100;
  }

  get repeticoes() {
    let repeat = Number(this.configuracao?.numeroRepeticoes) ?? 1;
    if (isNaN(repeat)) {
      repeat = 1;
    }
    return repeat;
  }

  get periodoRepeticaoNotificacaoPendente(): number {
    let periodo = Number(this.configuracao?.repetirSomPedidoPendentePeriodo);

    if (isNaN(periodo) || periodo < 0) {
      periodo = 300;
    }

    return periodo * 1000;
  }

  get notificacaoPedidoPendenteEstaHabilitada(): boolean {
    return this.configuracao?.repetirSomPedidoPendente === "LIGADO";
  }

  tocarSom() {
    if (!this.configuracao?.notificacao || this._estaTocandoSom) return;

    const sound = this.configuracao?.notificacao ?? "sound_3";

    clearInterval(this._repetidorSom);

    const audio = new Audio(`assets/sound/${sound}.wav`);
    audio.volume = this.volume;

    this._estaTocandoSom = true;
    audio.play().then(() => {
      const interval = audio.duration * 1000 + 500;

      if (this.repeticoes === 1) {
        return setTimeout(() => {
          this._estaTocandoSom = false;
        }, 1000);
      }

      let quantidadeTocada = 1;
      this._repetidorSom = setInterval(() => {
        audio.play();
        quantidadeTocada++;

        if (quantidadeTocada >= this.repeticoes) {
          clearInterval(this._repetidorSom);
          return setTimeout(() => {
            this._estaTocandoSom = false;
          }, 1000);
        }
      }, interval);
    });
  }
}
